/**
 * PerksAndRewardsSection — shared state map + Builder input schema generator.
 *
 * Faithful port of composable
 * `hec/builder/blocks/account/perks-and-rewards-section/utils.js`. The four
 * states and the per-state subfields (incl. the loggedOut-only `ctaMode`) are
 * generated identically so the Builder editing UI and content shape match
 * across MRT (composable) and Workers — the two render this same model during
 * the migration.
 */
import type { RegisteredComponent } from "@builder.io/sdk-react";

import { queryParamInputOption as queryParamInputOptionFromCta } from "../cta/cta.helper";

/** Element type of a Builder component's `inputs` array. */
type BuilderInput = NonNullable<RegisteredComponent["inputs"]>[number];

export const perksAndRewardsStateMap = {
  LOGGED_OUT: "loggedOut",
  NOT_ENROLLED: "notEnrolled",
  ENROLLED_WITH_NO_POINTS: "enrolledWithNoPoints",
  ENROLLED_WITH_POINTS: "enrolledWithPoints",
} as const;

export type PerksUserState =
  (typeof perksAndRewardsStateMap)[keyof typeof perksAndRewardsStateMap];

/**
 * Which state a shopper sees. The editor previews `stateToShowInEditor`; a
 * shopper is logged out, not enrolled, enrolled with no points, or enrolled
 * with points. A member whose saved balance is unknown (`UNKNOWN_POINTS`, a
 * failed Annex read) is shown as enrolled WITH points, where the block renders
 * "updating" in place of the number: its coerced 0 would otherwise pick the
 * no-points state and the copy authored for a member who has nothing.
 */
export function derivePerksUserState({
  editing,
  stateToShowInEditor,
  isLoggedIn,
  isEnrolled,
  points,
}: {
  editing: boolean;
  stateToShowInEditor?: PerksUserState;
  isLoggedIn: boolean;
  isEnrolled: boolean;
  /** Undefined when the saved balance was never read. */
  points: number | undefined;
}): PerksUserState {
  if (editing) return stateToShowInEditor ?? perksAndRewardsStateMap.LOGGED_OUT;
  if (!isLoggedIn) return perksAndRewardsStateMap.LOGGED_OUT;
  if (!isEnrolled) return perksAndRewardsStateMap.NOT_ENROLLED;
  return points !== undefined && points <= 0
    ? perksAndRewardsStateMap.ENROLLED_WITH_NO_POINTS
    : perksAndRewardsStateMap.ENROLLED_WITH_POINTS;
}

// The CTA `queryParamInputOption` carries a `showIf` that gates on the core
// button's `clickFunction`/`isInternalLink` — inputs this component doesn't
// have. Strip it so the param list is always visible inside each state
// (mirrors composable utils.js: `const {showIf, ...queryParamInputOption}`).
const { showIf: _showIf, ...queryParamInputOption } =
  queryParamInputOptionFromCta;

/**
 * Build the `perksAndRewardsData` subfields for all four states. Every state
 * gets `ctaLink` (a `page` reference — NOT a string), `queryParams`, and
 * `ctaText`; all states except `enrolledWithPoints` add `prompt`; only
 * `loggedOut` adds `ctaMode` (drawer | link, default drawer).
 */
export const generateSubfields = (): BuilderInput[] =>
  Object.values(perksAndRewardsStateMap).map(userState => {
    const includePrompt =
      userState !== perksAndRewardsStateMap.ENROLLED_WITH_POINTS;

    const subFields: BuilderInput[] = [
      ...(includePrompt
        ? [{ name: "prompt", type: "string" } as BuilderInput]
        : []),
      {
        name: "ctaLink",
        type: "reference",
        model: "page",
        required: true,
      } as BuilderInput,
      queryParamInputOption as BuilderInput,
      { name: "ctaText", type: "string", required: true } as BuilderInput,
    ];

    if (userState === perksAndRewardsStateMap.LOGGED_OUT) {
      subFields.push({
        name: "ctaMode",
        type: "string",
        enum: ["drawer", "link"],
        defaultValue: "drawer",
        helperText:
          "Choose whether to show a login drawer or a link when logged out.",
      } as BuilderInput);
    }

    return {
      name: userState,
      type: "object",
      subFields,
      required: true,
    } as BuilderInput;
  });
